import React, { useCallback, useEffect, useRef, useState } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { playerBonusesLimit, playerBonusesLimits } from "configs/limits";
import Pagination from "components/Pagination";
import EmptyState from "components/EmptyState/EmptyState";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";
import { useTimeFormat } from "helpers/timeFormat";
import { handleApiError, getBusinessErrorCodes } from "helpers/handleApiError";
import {
  Type,
  TypeLabel,
} from "components/table/components/TypeLabel/TypeLabel";
import {
  EMPTY_VALUE,
  formatPayoutValue,
  formatRemainingTime,
  formatWagerMultiplier,
  getBonusImage,
} from "helpers/playerBonusFormatters";
import { useAppDispatch } from "store";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import {
  PlayerBonusListMode,
  PlayerBonusesListData,
  UserAttachedBonus,
  UserBonusOffer,
} from "types/playerBonuses";

import {
  BonusDateCell,
  BonusProgressCell,
  BonusRewardCell,
  BonusStatusBadge,
  SplitColumnHeader,
} from "./BonusTableCells";
import BonusAccordion from "./BonusAccordion";
import BonusRowActions, {
  BonusRowActionType,
  CANCEL_REASON_MAX_LENGTH,
  getBonusRowAction,
} from "./BonusRowActions";
import styles from "./ListBonusesTab.module.scss";

const ATTACHED_COLUMNS_COUNT = 12;
const OFFERS_COLUMNS_COUNT = 9;

const BONUS_BUSINESS_ERRORS = ["bonusAlreadyInUse", "userBonusNotInWagering"];

interface ActiveBonusAction {
  rowId: number;
  action: BonusRowActionType;
}

interface ListBonusesTabProps {
  mode: PlayerBonusListMode;
  onMetaChange?: (meta: { totalItems: number; isLoading: boolean }) => void;
  canCreate: boolean;
  canUpdate: boolean;
}

const ListBonusesTab: React.FC<ListBonusesTabProps> = ({
  mode,
  onMetaChange,
  canCreate,
  canUpdate,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const timeFormat = useTimeFormat();
  const userId = searchParams.get("entityId");

  const [items, setItems] = useState<(UserAttachedBonus | UserBonusOffer)[]>(
    [],
  );
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(playerBonusesLimit);
  // Start in the loading state so the tab never flashes an empty area before
  // the first bonuses request begins.
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [loadedMode, setLoadedMode] = useState(mode);
  const [activeAction, setActiveAction] = useState<ActiveBonusAction | null>(
    null,
  );
  const [cancelReason, setCancelReason] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const activeActionRef = useRef<ActiveBonusAction | null>(null);
  const attachedPrefetchRef = useRef<{
    userId: string;
    data: PlayerBonusesListData<UserAttachedBonus>;
  } | null>(null);
  const modeRef = useRef(mode);

  modeRef.current = mode;

  const resetActionState = () => {
    activeActionRef.current = null;
    setActiveAction(null);
    setCancelReason("");
    setIsActionLoading(false);
  };

  // Mode can change before effects run — clear stale list synchronously
  // so Attached table never renders Offers items (no status).
  if (loadedMode !== mode) {
    setLoadedMode(mode);
    setActivePage(1);
    setItems([]);
    setTotalItems(0);
    setTotalPages(0);
    setExpandedIds([]);
    resetActionState();
    setIsLoading(true);
    setHasLoaded(false);

    if (mode !== "attached") {
      attachedPrefetchRef.current = null;
    }
  }

  const prefetchAttached = useCallback(async () => {
    if (!userId || modeRef.current !== "offers") {
      return;
    }

    try {
      const result = (await instance.get(
        `${apiRoutes.playerBonuses}/${userId}/history?page=1&limit=${currentLimit}`,
      )) as { data: PlayerBonusesListData<UserAttachedBonus> };

      if (modeRef.current !== "offers") {
        return;
      }

      attachedPrefetchRef.current = { userId, data: result.data };
    } catch {
      attachedPrefetchRef.current = null;
    }
  }, [currentLimit, userId]);

  const loadList = useCallback(async () => {
    if (!userId) {
      return;
    }

    const prefetch = attachedPrefetchRef.current;
    const canUsePrefetch =
      mode === "attached" && activePage === 1 && prefetch?.userId === userId;

    if (canUsePrefetch) {
      attachedPrefetchRef.current = null;
      setItems(prefetch.data.items);
      setTotalItems(prefetch.data.totalItems);
      setTotalPages(prefetch.data.totalPages);
      setExpandedIds([]);
      setIsLoading(false);
      setHasLoaded(true);
      return;
    }

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);

    const endpoint =
      mode === "attached"
        ? `${apiRoutes.playerBonuses}/${userId}/history`
        : `${apiRoutes.playerBonuses}/${userId}/offers`;

    try {
      const result = (await instance.get(
        `${endpoint}?page=${activePage}&limit=${currentLimit}`,
        { signal: abortController.signal },
      )) as { data: PlayerBonusesListData<UserAttachedBonus | UserBonusOffer> };

      if (abortController.signal.aborted) {
        return;
      }

      setItems(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
      setExpandedIds([]);
      attachedPrefetchRef.current = null;
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      setItems([]);
      setTotalItems(0);
      setTotalPages(0);
      handleApiError({ error, t, dispatch });
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  }, [activePage, currentLimit, dispatch, mode, t, userId]);

  useEffect(() => {
    loadList();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [loadList]);

  useEffect(() => {
    resetActionState();
  }, [activePage]);

  useEffect(() => {
    onMetaChange?.({ totalItems, isLoading });
  }, [isLoading, onMetaChange, totalItems]);

  useEffect(() => {
    return () => {
      onMetaChange?.({ totalItems: 0, isLoading: false });
    };
  }, [onMetaChange]);

  const handleToggleExpand = (id: number) => {
    setExpandedIds((current) =>
      current.includes(id)
        ? current.filter((expandedId) => expandedId !== id)
        : [...current, id],
    );
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage(activePage - 1);
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      setActivePage(activePage + 1);
    }
  };

  const handleActionToggle =
    (rowId: number, action: BonusRowActionType) => (open: boolean) => {
      if (isActionLoading && !open) {
        return;
      }

      if (!open) {
        const currentAction = activeActionRef.current;

        if (
          currentAction?.rowId !== rowId ||
          currentAction?.action !== action
        ) {
          return;
        }

        resetActionState();
        return;
      }

      const nextAction = { rowId, action };
      activeActionRef.current = nextAction;
      setActiveAction(nextAction);
      setCancelReason("");
    };

  const handleActionError = (error: unknown) => {
    const businessErrorCodes = getBusinessErrorCodes(error);
    handleApiError({ error, t, dispatch });

    if (
      businessErrorCodes.some((code) => BONUS_BUSINESS_ERRORS.includes(code))
    ) {
      resetActionState();
      loadList();
    }
  };

  const handleClaimConfirm = async (bonusId: number) => {
    if (!userId || isActionLoading) {
      return;
    }

    setIsActionLoading(true);

    try {
      await instance.post(
        `${apiRoutes.playerBonuses}/${userId}/offers/${bonusId}/claim`,
        {},
      );

      resetActionState();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("bonusWasSuccessfullyAssigned"),
          type: NotificationTypes.success,
        }),
      );
      await loadList();
      void prefetchAttached();
    } catch (error) {
      handleActionError(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCancelConfirm = async (userBonusId: number) => {
    if (!userId || isActionLoading) {
      return;
    }

    const trimmedReason = cancelReason.trim();

    if (!trimmedReason || trimmedReason.length > CANCEL_REASON_MAX_LENGTH) {
      return;
    }

    setIsActionLoading(true);

    try {
      await instance.post(
        `${apiRoutes.playerBonuses}/${userId}/bonuses/${userBonusId}/cancel`,
        { cancelReason: trimmedReason },
      );

      resetActionState();
      dispatch(
        showNotification({
          title: t("success"),
          message: t("bonusWasSuccessfullyCancelled"),
          type: NotificationTypes.success,
        }),
      );
      await loadList();
    } catch (error) {
      handleActionError(error);
    } finally {
      setIsActionLoading(false);
    }
  };

  const renderRowActions = (rowId: number, availableActions?: string[]) => {
    const permittedActions =
      mode === "offers"
        ? canCreate
          ? availableActions
          : []
        : canUpdate
          ? availableActions
          : [];
    const action = getBonusRowAction(permittedActions);
    const isOpen = Boolean(
      action &&
        activeAction?.rowId === rowId &&
        activeAction?.action === action,
    );

    return (
      <td className={styles.colActions}>
        <BonusRowActions
          rowId={rowId}
          action={action}
          isOpen={isOpen}
          isLoading={isActionLoading && isOpen}
          cancelReason={cancelReason}
          onCancelReasonChange={setCancelReason}
          onToggle={
            action ? handleActionToggle(rowId, action) : () => undefined
          }
          onConfirm={() => {
            if (action === "claim") {
              void handleClaimConfirm(rowId);
              return;
            }

            if (action === "cancel") {
              void handleCancelConfirm(rowId);
            }
          }}
        />
      </td>
    );
  };

  const emptyMessage =
    loadedMode === "attached"
      ? t("noBonusesAttachedToPlayer")
      : t("noBonusOffersAvailableForPlayer");

  const renderTypeCell = (type: string) => {
    const knownTypes: Type[] = [
      "regular",
      "recurring",
      "signUp",
      "confirmation",
      "promoCode",
      "loyalty",
      "cashback",
      "reload",
    ];

    if (knownTypes.includes(type as Type)) {
      return <TypeLabel type={type as Type} />;
    }

    return type;
  };

  const renderExpandButton = (id: number, isExpanded: boolean) => (
    <button
      type="button"
      className={cn(
        styles.expandButton,
        isExpanded && styles.expandButtonOpened,
      )}
      onClick={() => handleToggleExpand(id)}
      aria-expanded={isExpanded}
      aria-label={t("details")}
    >
      <span className={styles.expandIcon} aria-hidden />
    </button>
  );

  const renderAccordionPanel = (
    item: UserAttachedBonus | UserBonusOffer,
    isExpanded: boolean,
    colSpan: number,
    showFreeSpins = false,
  ) => (
    <tr
      className={cn(styles.accordionRow, isExpanded && styles.accordionRowOpen)}
    >
      <td colSpan={colSpan} className={styles.accordionCell}>
        <div
          className={cn(
            styles.accordionWrap,
            isExpanded && styles.accordionWrapOpen,
          )}
        >
          <div className={styles.accordionInner}>
            <BonusAccordion item={item} showFreeSpins={showFreeSpins} />
          </div>
        </div>
      </td>
    </tr>
  );

  const renderAttachedRow = (item: UserAttachedBonus) => {
    const isExpanded = expandedIds.includes(item.userBonusId);

    return (
      <React.Fragment key={item.userBonusId}>
        <tr className={cn(isExpanded && styles.rowExpanded)}>
          <td className={styles.colExpand}>
            {renderExpandButton(item.userBonusId, isExpanded)}
          </td>
          <td className={styles.colImage}>
            <ImageWithFallback
              src={getBonusImage(item)}
              alt={item.title}
              className={styles.bonusImage}
              width={50}
              height={50}
            />
          </td>
          <td className={styles.colTitle}>{item.title}</td>
          <td className={styles.colStatus}>
            <BonusStatusBadge
              status={item.status}
              historyType={item.historyType}
            />
          </td>
          <td className={cn(styles.colType, styles.typeCell)}>
            {renderTypeCell(item.type)}
          </td>
          <td className={styles.colDate}>
            <BonusDateCell date={item.claimedAt} timeFormat={timeFormat} />
          </td>
          <td className={styles.colDate}>
            <BonusDateCell date={item.activatedAt} timeFormat={timeFormat} />
          </td>
          <td className={styles.colShort}>
            {formatWagerMultiplier(item.wagerX)}
          </td>
          <td className={styles.colShort}>{formatPayoutValue(item.payoutX)}</td>
          <td className={styles.colProgress}>
            <BonusProgressCell wagerProgress={item.wagerProgress} />
          </td>
          <td className={styles.colReward}>
            <BonusRewardCell rewards={item.rewards} />
          </td>
          {renderRowActions(item.userBonusId, item.availableActions)}
        </tr>
        {renderAccordionPanel(item, isExpanded, ATTACHED_COLUMNS_COUNT, true)}
      </React.Fragment>
    );
  };

  const renderOffersRow = (item: UserBonusOffer) => {
    const claimTime = formatRemainingTime(item.claimingEndInMilliseconds);
    const isExpanded = expandedIds.includes(item.bonusId);

    return (
      <React.Fragment key={item.bonusId}>
        <tr className={cn(isExpanded && styles.rowExpanded)}>
          <td className={styles.colExpand}>
            {renderExpandButton(item.bonusId, isExpanded)}
          </td>
          <td className={styles.colImage}>
            <ImageWithFallback
              src={getBonusImage(item)}
              alt={item.title}
              className={styles.bonusImage}
              width={50}
              height={50}
            />
          </td>
          <td className={styles.colTitle}>{item.title}</td>
          <td className={cn(styles.colType, styles.typeCell)}>
            {renderTypeCell(item.type)}
          </td>
          <td className={styles.colShort}>
            {formatWagerMultiplier(item.wagerX)}
          </td>
          <td className={styles.colShort}>{formatPayoutValue(item.payoutX)}</td>
          <td className={styles.colReward}>
            <BonusRewardCell rewards={item.rewards} />
          </td>
          <td className={styles.colDate}>
            {claimTime ? (
              <span className={styles.claimTimer}>{claimTime}</span>
            ) : (
              EMPTY_VALUE
            )}
          </td>
          {renderRowActions(item.bonusId, item.availableActions)}
        </tr>
        {renderAccordionPanel(item, isExpanded, OFFERS_COLUMNS_COUNT)}
      </React.Fragment>
    );
  };

  const renderAttachedTable = () => (
    <table className={styles.bonusesTable}>
      <colgroup>
        <col className={styles.colExpand} />
        <col className={styles.colImage} />
        <col className={styles.colTitle} />
        <col className={styles.colStatus} />
        <col className={styles.colType} />
        <col className={styles.colDate} />
        <col className={styles.colDate} />
        <col className={styles.colShort} />
        <col className={styles.colShort} />
        <col className={styles.colProgress} />
        <col className={styles.colReward} />
        <col className={styles.colActions} />
      </colgroup>
      <thead>
        <tr>
          <th className={styles.colExpand} aria-hidden="true" />
          <th className={styles.colImage}>{t("image")}</th>
          <th className={styles.colTitle}>{t("title")}</th>
          <th className={styles.colStatus}>{t("status")}</th>
          <th className={styles.colType}>{t("type")}</th>
          <th className={styles.colDate}>{t("claimedAt")}</th>
          <th className={styles.colDate}>{t("activatedAt")}</th>
          <th className={styles.colShort}>{t("wager")}</th>
          <th className={styles.colShort}>{t("payout")}</th>
          <th className={styles.colProgress}>{t("progress")}</th>
          <th className={styles.colReward}>{t("reward")}</th>
          <th className={styles.colActions} aria-hidden="true" />
        </tr>
      </thead>
      <tbody>{(items as UserAttachedBonus[]).map(renderAttachedRow)}</tbody>
    </table>
  );

  const renderOffersTable = () => (
    <table className={cn(styles.bonusesTable, styles.offersTable)}>
      <colgroup>
        <col className={styles.colExpand} />
        <col className={styles.colImage} />
        <col className={styles.colTitle} />
        <col className={styles.colType} />
        <col className={styles.colShort} />
        <col className={styles.colShort} />
        <col className={styles.colReward} />
        <col className={styles.colDate} />
        <col className={styles.colActions} />
      </colgroup>
      <thead>
        <tr>
          <th className={styles.colExpand} aria-hidden="true" />
          <th className={styles.colImage}>{t("image")}</th>
          <th className={styles.colTitle}>{t("title")}</th>
          <th className={styles.colType}>{t("type")}</th>
          <th className={styles.colShort}>{t("wager")}</th>
          <th className={styles.colShort}>{t("payout")}</th>
          <th className={styles.colReward}>{t("reward")}</th>
          <th className={styles.colDate}>
            <SplitColumnHeader top={t("claim")} bottom={t("time")} />
          </th>
          <th className={styles.colActions} aria-hidden="true" />
        </tr>
      </thead>
      <tbody>{(items as UserBonusOffer[]).map(renderOffersRow)}</tbody>
    </table>
  );

  const renderTableContent = () => {
    if (isLoading || !hasLoaded || loadedMode !== mode) {
      return (
        <div className={styles.loadingState}>
          <ModalTabSkeleton />
        </div>
      );
    }

    if (!items.length) {
      return <EmptyState message={emptyMessage} />;
    }

    return (
      <div className={styles.tableWrap}>
        {loadedMode === "attached"
          ? renderAttachedTable()
          : renderOffersTable()}
      </div>
    );
  };

  return (
    <div className={cn("player-content-wrapper", styles.bonusesTab)}>
      <div className={cn("inner-table-wrapper", styles.tableWrapper)}>
        {renderTableContent()}
      </div>

      {!isLoading && items.length > 0 && (
        <div className={styles.bottomBar}>
          <Pagination
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={playerBonusesLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              setActivePage(1);
            }}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        </div>
      )}
    </div>
  );
};

export default ListBonusesTab;
